import { get, post, patch } from "@/services/axiosUitils";

const basePath = "/city_collab/collabrations";

export interface CollaborationCounts {
  accepted: number;
  request_pending: number;
  acceptance_pending: number;
  login_org_id?: string;
}

export interface OrganizationResponse {
  org_id: string;
  org_name: string;
  location: string;
  phone: string;
  image: string | null;
  organizationtype: string;
  organizationtype_name: string;
  primaryemail: string | null;
  website?: string | null;
  description?: string | null;
  member_count: number;
  event_count: number;
  status: string | null;
  status_label: string | null;
  login_org_id?: string;
}

export interface CollaborationResponse {
  id: string;
  sender_organization_id: string;
  sender_organization_name: string;
  receiver_organization_id: string;
  receiver_organization_name: string;
  status: number;
  status_label: string;
  description: string;
  accepted_at: string | null;
  accepted_by: string | null;
  accepted_by_name: string | null;
  created_on: string;
}

class CityCollaborationService {
  /**
   * Get collaboration counts (accepted, request_pending, acceptance_pending)
   */
  getCollaborationCounts = async (): Promise<CollaborationCounts | null> => {
    try {
      const response = await get<any>(`${basePath}/count/`, {
        notifyMessage: false,
      });

      console.log("Collaboration counts API response:", response);

      if (!response) {
        console.warn("No response from collaboration counts API");
        return null;
      }

      // Handle different response structures
      // Case 1: Direct response with fields
      if (
        response.accepted !== undefined ||
        response.request_pending !== undefined ||
        response.acceptance_pending !== undefined
      ) {
        const counts = {
          accepted: Number(response.accepted) || 0,
          request_pending: Number(response.request_pending) || 0,
          acceptance_pending: Number(response.acceptance_pending) || 0,
          login_org_id: response.login_org_id ?? undefined,
        };
        console.log("Parsed collaboration counts (direct):", counts);
        return counts;
      }

      // Case 2: Nested in data property
      if (response.data && typeof response.data === "object") {
        const counts = {
          accepted: Number(response.data.accepted) || 0,
          request_pending: Number(response.data.request_pending) || 0,
          acceptance_pending: Number(response.data.acceptance_pending) || 0,
          login_org_id:
            response.data.login_org_id ?? response.login_org_id ?? undefined,
        };
        console.log("Parsed collaboration counts (nested in data):", counts);
        return counts;
      }

      // Case 3: Nested in results property
      if (response.results && typeof response.results === "object") {
        const counts = {
          accepted: Number(response.results.accepted) || 0,
          request_pending: Number(response.results.request_pending) || 0,
          acceptance_pending: Number(response.results.acceptance_pending) || 0,
          login_org_id:
            response.results.login_org_id ?? response.login_org_id ?? undefined,
        };
        console.log("Parsed collaboration counts (nested in results):", counts);
        return counts;
      }

      console.warn("Unexpected response structure:", response);
      return null;
    } catch (error) {
      console.error("Error fetching collaboration counts:", error);
      return null;
    }
  };

  /**
   * Get organizations for collaboration
   */
  getOrganizations = async (
    search?: string
  ): Promise<OrganizationResponse[]> => {
    try {
      const response = await get<any>(`${basePath}/orgs/`, {
        params: {
          search: search ?? "",
        },
        notifyMessage: false,
      });

      if (!response) {
        return [];
      }

      // Handle response with success and data structure
      if (response.success && response.data && Array.isArray(response.data)) {
        return response.data;
      }

      // Handle array response
      if (Array.isArray(response)) {
        return response;
      }

      // Handle nested in results
      if (response.results && Array.isArray(response.results)) {
        return response.results;
      }

      // Handle nested in data
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      }

      return [];
    } catch (error) {
      console.error("Error fetching organizations:", error);
      return [];
    }
  };

  /**
   * Get all collaborations
   */
  getCollaborations = async (): Promise<CollaborationResponse[]> => {
    try {
      const response = await get<any>(`${basePath}/`, {
        notifyMessage: false,
      });

      if (!response) {
        return [];
      }

      // Handle array response
      if (Array.isArray(response)) {
        return response;
      }

      // Handle nested in results
      if (response.results && Array.isArray(response.results)) {
        return response.results;
      }

      // Handle nested in data
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      }

      return [];
    } catch (error) {
      console.error("Error fetching collaborations:", error);
      return [];
    }
  };

  /**
   * Create a collaboration request
   */
  createCollaboration = async (payload: {
    sender_organization_id: string;
    receiver_organization_id: string;
  }): Promise<CollaborationResponse | null> => {
    try {
      console.log("Calling API: POST /city_collab/collabrations/");
      console.log("Payload:", payload);

      const response = await post<CollaborationResponse>(
        `${basePath}/`,
        payload,
        {
          notifyMessage: true,
        }
      );

      console.log("API Response:", response);
      return response ?? null;
    } catch (error) {
      console.error("Error creating collaboration:", error);
      return null;
    }
  };

  /**
   * Update collaboration status
   */
  updateCollaborationStatus = async (
    collaborationId: string,
    status: number
  ): Promise<CollaborationResponse | null> => {
    try {
      console.log(
        `Calling API: PATCH /city_collab/collabrations/${collaborationId}/`
      );
      console.log("Payload:", { status });

      const response = await patch<CollaborationResponse>(
        `${basePath}/${collaborationId}/`,
        { status },
        {
          notifyMessage: true,
        }
      );

      console.log("API Response:", response);
      return response ?? null;
    } catch (error) {
      console.error("Error updating collaboration status:", error);
      return null;
    }
  };

  /**
   * Reject a collaboration request
   */
  rejectCollaboration = async (
    collaborationId: string
  ): Promise<CollaborationResponse | null> => {
    try {
      console.log(
        `Calling API: PATCH /city_collab/collabrations/${collaborationId}/reject/`
      );
      console.log("Payload:", { status: 3 });

      const response = await patch<CollaborationResponse>(
        `${basePath}/${collaborationId}/reject/`,
        { status: 3 },
        {
          notifyMessage: true,
        }
      );

      console.log("API Response:", response);
      return response ?? null;
    } catch (error) {
      console.error("Error rejecting collaboration:", error);
      return null;
    }
  };

  /**
   * Withdraw/Cancel a collaboration request
   */
  withdrawCollaboration = async (
    collaborationId: string
  ): Promise<CollaborationResponse | null> => {
    try {
      console.log(
        `Calling API: PATCH /city_collab/collabrations/${collaborationId}/withdraw/`
      );
      console.log("Payload:", { status: 4 });

      const response = await patch<CollaborationResponse>(
        `${basePath}/${collaborationId}/withdraw/`,
        { status: 4 },
        {
          notifyMessage: true,
        }
      );

      console.log("API Response:", response);
      return response ?? null;
    } catch (error) {
      console.error("Error withdrawing collaboration:", error);
      return null;
    }
  };

  /**
   * Get user role by user id
   */
  getUserRole = async (userId: string): Promise<any> => {
    try {
      const response = await get<any>("/city_collab/user-role/", {
        params: { user_id: userId },
        notifyMessage: false,
      });
      return response;
    } catch (error) {
      console.error("Error fetching user role:", error);
      return null;
    }
  };

  /**
   * Update member status to Retired or Resigned during roster import
   */
  updateMemberStatus = async (
    memberId: string,
    mstatus: "Retired" | "Resigned"
  ): Promise<{
    success: boolean;
    message: string;
    data?: { member_id: string; mstatus: string; organization_id: string };
  }> => {
    return await patch("/city_collab/roster-import/update-member-mstatus/", {
      member_id: memberId,
      mstatus,
    });
  };
}

const instance = new CityCollaborationService();
export default instance;
